import { useEffect, useMemo, useState } from 'react'
import { SectionBand } from './FormSheet'
import {
  issueRuling,
  listDepartmentDocket,
  noticeReport,
  RULING_LIMIT,
  type AdminComplaint,
} from '../lib/admin'
import { isUnauthorized } from '../lib/apiFetch'
import { officialDate, officialTimestamp } from '../lib/complaints'

/**
 * The whole docket, searchable, with a ruling form under whichever complaint is
 * open. One complaint expands at a time: the department attends to one matter
 * at a time.
 */
export function RulingDocket({ onSessionLost }: { onSessionLost: () => void }) {
  const [complaints, setComplaints] = useState<AdminComplaint[] | null>(null)
  const [notice, setNotice] = useState('')
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<number | null>(null)

  useEffect(() => {
    let current = true
    listDepartmentDocket()
      .then((docket) => current && setComplaints(docket))
      .catch((error) => {
        if (!current) return
        if (isUnauthorized(error)) {
          onSessionLost()
          return
        }
        setNotice(error instanceof Error ? error.message : 'The docket could not be read.')
      })
    return () => {
      current = false
    }
  }, [onSessionLost])

  const matches = useMemo(() => search(complaints ?? [], query), [complaints, query])

  /** A ruling replaces the complaint in place; the docket does not reorder under you. */
  function replace(ruled: AdminComplaint) {
    setComplaints((current) =>
      (current ?? []).map((complaint) => (complaint.id === ruled.id ? ruled : complaint)),
    )
  }

  return (
    <>
      <SectionBand title="Docket and rulings" />

      {notice ? (
        <p role="alert" className="px-4 py-8 font-form text-base font-semibold text-stamp sm:px-7">
          {notice}
        </p>
      ) : !complaints ? (
        <p className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Retrieving docket…
        </p>
      ) : (
        <div className="px-4 pb-6 pt-5 sm:px-7">
          <label
            htmlFor="docket-search"
            className="font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
          >
            Search the docket
          </label>
          <p className="mt-0.5 font-form text-[11px] italic leading-snug text-carbon/60">
            Case number, complainant, respondent, grievance, ruling, or address. All terms must match.
          </p>
          <input
            id="docket-search"
            type="search"
            value={query}
            autoComplete="off"
            placeholder="e.g. waffle"
            onChange={(e) => setQuery(e.target.value)}
            className="mt-2 w-full border border-carbon/25 bg-transparent px-2 py-1.5 font-type text-[15px] text-carbon outline-none placeholder:text-carbon/30 focus:border-process"
          />

          <p className="mt-3 font-form text-[11px] uppercase tracking-[0.16em] text-carbon/55">
            {summarise(matches.length, complaints.length, query)}
          </p>

          {matches.length === 0 ? (
            <p className="mt-6 border border-dashed border-carbon/25 px-3 py-8 text-center font-form text-sm text-carbon/60">
              {complaints.length === 0
                ? 'No complaints have been filed.'
                : 'No complaint on the docket matches that.'}
            </p>
          ) : (
            <ol className="mt-4 space-y-3">
              {matches.map((complaint) => (
                <li key={complaint.id}>
                  <DocketRow
                    complaint={complaint}
                    open={expanded === complaint.id}
                    onToggle={() => setExpanded(expanded === complaint.id ? null : complaint.id)}
                    onRuled={replace}
                    onSessionLost={onSessionLost}
                  />
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </>
  )
}

/** Every term has to appear somewhere in the complaint. Order does not matter. */
function search(complaints: AdminComplaint[], query: string): AdminComplaint[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return complaints

  return complaints.filter((complaint) => {
    const haystack = [
      complaint.caseNumber,
      complaint.filedBy,
      complaint.respondent,
      complaint.complaintText,
      complaint.ruling ?? '',
      complaint.complainantEmail ?? '',
      complaint.status,
    ]
      .join(' ')
      .toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

function summarise(shown: number, total: number, query: string): string {
  const noun = total === 1 ? 'complaint' : 'complaints'
  if (!query.trim()) return `${total} ${noun} on file, most recently filed first`
  return `${shown} of ${total} ${noun} match`
}

function DocketRow({
  complaint,
  open,
  onToggle,
  onRuled,
  onSessionLost,
}: {
  complaint: AdminComplaint
  open: boolean
  onToggle: () => void
  onRuled: (ruled: AdminComplaint) => void
  onSessionLost: () => void
}) {
  const resolved = complaint.ruling !== null

  return (
    <article className="border border-carbon/25">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-carbon/20 bg-process/[0.07] px-3 py-2 text-left hover:bg-process/[0.14]"
        >
          <span className="break-all font-type text-[12px] font-bold text-process">
            {complaint.caseNumber}
          </span>
          <span className="flex items-baseline gap-3">
            <span className="font-form text-[10px] uppercase tracking-[0.16em] text-carbon/60">
              {complaint.filedBy} v. {complaint.respondent}
            </span>
            <StatusMark resolved={resolved} />
          </span>
        </button>
      </h3>

      {open && <RulingPanel complaint={complaint} onRuled={onRuled} onSessionLost={onSessionLost} />}
    </article>
  )
}

function StatusMark({ resolved }: { resolved: boolean }) {
  return (
    <span
      className={`px-1.5 py-0.5 font-form text-[9px] font-bold uppercase tracking-[0.16em] ${
        resolved ? 'bg-process text-file' : 'border border-carbon/40 text-carbon/70'
      }`}
    >
      {resolved ? 'Resolved' : 'Received'}
    </span>
  )
}

function RulingPanel({
  complaint,
  onRuled,
  onSessionLost,
}: {
  complaint: AdminComplaint
  onRuled: (ruled: AdminComplaint) => void
  onSessionLost: () => void
}) {
  const [ruling, setRuling] = useState(complaint.ruling ?? '')
  const [report, setReport] = useState('')
  const [refusal, setRefusal] = useState('')
  const [issuing, setIssuing] = useState(false)

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (issuing) return

    if (!ruling.trim()) {
      setRefusal('The ruling cannot be left blank.')
      return
    }

    setRefusal('')
    setReport('')
    setIssuing(true)
    try {
      const result = await issueRuling(complaint.caseNumber, ruling.trim())
      onRuled(result.complaint)
      setReport(noticeReport(result.notice, result.complaint.complainantEmail))
    } catch (error) {
      if (isUnauthorized(error)) {
        onSessionLost()
        return
      }
      setRefusal(error instanceof Error ? error.message : 'The ruling could not be recorded.')
    } finally {
      setIssuing(false)
    }
  }

  return (
    <div className="px-3 pb-4 pt-3">
      <dl className="flex flex-wrap gap-x-6 gap-y-1">
        <Field label="Received" value={officialDate(complaint.createdAt)} />
        <Field label="Estimated review time" value={complaint.reviewEstimate} />
        <Field label="Address on file" value={complaint.complainantEmail ?? 'None given'} />
        {complaint.ruledAt !== null && (
          <Field label="Ruled" value={officialTimestamp(complaint.ruledAt)} />
        )}
      </dl>

      <p className="mt-3 font-form text-[10px] uppercase tracking-[0.18em] text-carbon/55">
        Statement of grievance as filed
      </p>
      {/* Shown on the same printed rules it was typed onto. */}
      <p className="ruled-field mt-1.5 whitespace-pre-wrap break-words border border-carbon/15 px-2 font-type text-[14px] text-carbon">
        {complaint.complaintText}
      </p>

      <form onSubmit={onSubmit} noValidate className="mt-4 border-t border-dashed border-carbon/30 pt-4">
        <label
          htmlFor={`ruling-${complaint.id}`}
          className="font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
        >
          Ruling of the department
        </label>
        <p className="mt-0.5 font-form text-[11px] italic leading-snug text-carbon/60">
          {complaint.complainantEmail
            ? 'Printed on the complainant’s receipt and sent to the address on file.'
            : 'Printed on the complainant’s receipt. No address is on file, so nobody will be written to.'}
        </p>
        <textarea
          id={`ruling-${complaint.id}`}
          value={ruling}
          rows={4}
          maxLength={RULING_LIMIT}
          onChange={(e) => setRuling(e.target.value)}
          className="ruled-field mt-2 w-full resize-y border border-carbon/25 px-2 font-type text-[15px] text-carbon outline-none focus:border-process"
        />
        <p className="mt-1 text-right font-form text-[10px] uppercase tracking-[0.16em] text-carbon/45 tabular-nums">
          {ruling.length} / {RULING_LIMIT} characters
        </p>

        {refusal && (
          <p
            role="alert"
            className="mt-3 border-l-4 border-stamp bg-stamp/5 px-3 py-2 font-form text-sm font-semibold text-stamp"
          >
            {refusal}
          </p>
        )}

        {report && (
          <p
            role="status"
            className="mt-3 border-l-4 border-process bg-process/5 px-3 py-2 font-form text-sm font-semibold text-process"
          >
            {report}
          </p>
        )}

        <button
          type="submit"
          disabled={issuing}
          className="mt-4 w-full bg-carbon px-6 py-3 font-form text-sm font-bold uppercase tracking-[0.22em] text-file transition-colors hover:bg-process disabled:cursor-not-allowed disabled:bg-carbon/40"
        >
          {issuing ? 'Issuing…' : complaint.ruling ? 'Amend this ruling' : 'Issue this ruling'}
        </button>
      </form>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-form text-[9px] uppercase tracking-[0.16em] text-carbon/55">{label}</dt>
      <dd className="font-type text-[13px] text-carbon">{value}</dd>
    </div>
  )
}
